// Copyright (c) Docugami, Inc. All rights reserved.

import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { Config } from "config";
import { ApiKey, ApiKeyRequest, ApiKeysResponse } from "networking/tokensApi";
import { createDgBaseQuery } from "store/baseQuery";

export const apiKeysAdapter = createEntityAdapter<ApiKey>();

const apiKeysBaseQuery = createDgBaseQuery(`${Config.apiUrl}/workspace/keys`);
export const apiKeysSlice = createApi({
    reducerPath: 'apiKeys',
    tagTypes: ['ApiKeys'],
    baseQuery: apiKeysBaseQuery,
    endpoints: builder => ({
        listKeys: builder.query<EntityState<ApiKey>, void>({
            query: () => '',
            providesTags: (result, error, arg) => ['ApiKeys'],
            transformResponse: (result: ApiKeysResponse): EntityState<ApiKey> => apiKeysAdapter.setAll(apiKeysAdapter.getInitialState(), result.keys),
        }),
        getKey: builder.query<ApiKey, string>({
            query: arg => encodeURIComponent(arg),
            providesTags: (result, error, arg) => [{ type: 'ApiKeys', id: arg }]
        }),
        addKey: builder.mutation<ApiKey, ApiKeyRequest>({
            query: request => ({
                url: '',
                method: 'POST',
                body: request
            }),
            invalidatesTags: (result, error, arg) => ['ApiKeys']
        }),
        deleteKey: builder.mutation<void, string>({
            query: request => ({
                url: encodeURIComponent(request),
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => ['ApiKeys', { type: 'ApiKeys', id: arg }],
            onQueryStarted: async (arg, api) => {
                const patches = api.dispatch(apiKeysSlice.util.updateQueryData('listKeys', undefined, state => apiKeysAdapter.removeOne(state, arg)));

                try {
                    await api.queryFulfilled;
                }
                catch {
                    api.dispatch(apiKeysSlice.util.patchQueryData('listKeys', undefined, patches.inversePatches));
                }
            }
        })
    })
});

export const {
    useListKeysQuery,
    useLazyListKeysQuery,
    useGetKeyQuery,
    useLazyGetKeyQuery,
    useAddKeyMutation,
    useDeleteKeyMutation } = apiKeysSlice;
